import type { Column, SortDirection } from '../../../claim-history/simple-table'
import type { Document } from '../queries'
import type { UploadDocumentVariables } from './documents/mutations'
import type { Control, FormState, UseFormHandleSubmit } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { type ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { LuColumns, LuRows } from 'react-icons/lu'
import { MdDownload } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import Select from 'react-select'
import { animated, useSpring } from 'react-spring'
import { z } from 'zod'

import { GeneralDocuments } from '../../../../lib/Codes'
import { DiscardDialogContext } from '../../../../lib/global-discard-dialog'
import http from '../../../../lib/http'
import { getIdToken } from '../../../../lib/token-utils'
import * as Table from '../../../claim-history/simple-table'
import tableStyles from '../../../claim-history/simple-table/table.module.css'
import * as Card from '../../../shared/card'
import { FriendlyDateTime } from '../../../shared/Date/friendly-date-time'
import FriendlyDate from '../../../shared/Date/FriendlyDate'
import * as Grid from '../../../shared/grid'
import { MaterialDeleteButton } from '../../../shared/material-delete-button'
import { PortedModal } from '../../../shared/NarusMaterial'
import { useBehavioralContext } from '../context'
import { useDocumentsQuery } from '../queries'

import { Checkbox } from './documents/checkbox'
import { DocumentsUploader, DocumentViewer } from './documents/documents-uploader'
import { useDeleteDocument, useEditDocumentMutation, useUploadDocumentMutation } from './documents/mutations'

const UPDATE_FORM_KEY = 'update-behavioral-documents-grid'
const CREATE_FORM_KEY = 'create-behavioral-documents-grid'

interface DocumentsGridProps {
  patientId: string
  clientName: string
}

interface State {
  open: boolean
}

interface GridState {
  sortBy: keyof Document
  sortDir: SortDirection
  page: number
  size: number
}

type ModalState = {
  title: string
  description: string
  open: boolean
  actions: ReactNode[]
} | null

const OPENABLE_FILE_TYPES = [ 'image/jpeg', 'image/gif', 'image/png', 'application/pdf' ]

const openUrlInNewTab = (url: string): void => {
  getIdToken().then(idToken => {
    const sing = url.includes('?') ? '&' : '?'
    window.open(`${url}${sing}authorization=Bearer ${idToken}`, '_blank')
  }).catch(error => {
    console.error('Error getting token for download:', error)
  })
}

export function DocumentsGrid ({ patientId, clientName }: DocumentsGridProps): JSX.Element {
  let [ form, setForm ] = useState<State>({
    open: false
  })
  let [ modal, setModal ] = useState<ModalState>()
  let [ gridState, setGridState ] = useState<GridState>({ sortBy: 'date_upload', sortDir: 'desc', page: 0, size: 5 })
  let behavioralContext = useBehavioralContext()
  let [ card, setCard ] = useState<{ open: boolean }>({ open: true })
  let discardDialog = useContext(DiscardDialogContext)
  // let { onFormChange, resetForm } = discardDialog ?? {}

  const query = useDocumentsQuery({ patientId, options: { field: gridState.sortBy, dir: gridState.sortDir, page: gridState.page, size: gridState.size } })
  const documentMutation = useUploadDocumentMutation({ patientId, clientName })
  const deleteDocumentMutation = useDeleteDocument()
  const { formState, control, handleSubmit, reset } = useForm<CreateFormValues>({
    defaultValues: {
      file: null,
      description: '',
      notVisible: false,
      created: undefined,
      type: undefined
    }
  })

  const columns = useMemo<Array<Column<Document>>>(() => [
    {
      name: 'date_upload',
      header: 'Upload Date',
      initialSortDirection: 'desc',
      isSortable: true,
      getValue: (doc) => (
        doc.date_upload ? <FriendlyDateTime datetime={doc.date_upload} /> : null
      )
    },
    {
      gridArea: '1.5fr',
      name: 'file',
      isSortable: true,
      getValue: (doc) => {
        const link = `/api/patients/${patientId ?? ''}/documents/${doc.file.hash ?? ''}?documentType=DocumentReference&clientName=${clientName ?? ''}&downloadType=inline`
        return (
          <button
            className='hover:text-primary word-wrap transition-colors cursor-pointer'
            title='Download'
            data-test-id='download-file-behavioral-grid'
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()

              openUrlInNewTab(link)
            }}
          >
            {doc?.file?.title ?? null}{' '}
            <span className={cx(tableStyles.actionButton, 'align-sub')}><MdDownload /></span>
          </button>
        )
      },
      header: 'File Name'
    },
    {
      name: 'view',
      header: '',
      gridArea: '0.1fr',
      getValue: (doc) => {
        if (!doc.file.title) {
          return null
        }

        if (!OPENABLE_FILE_TYPES.includes(doc.file.contentType ?? '')) {
          return <div />
        }

        let selectedDocument = behavioralContext.document
        let selectedId: string | null = null
        let selectedPosition: string | null = null

        if (selectedDocument) {
          selectedId = selectedDocument.documentId
          selectedPosition = selectedDocument.position
        }

        return (
          <div className='flex flex-row gap-4 items-center'>
            <div className='flex flex-row gap-2 items-center'>
              <button
                className={cx('p-1 -m-1 flex items-center justify-center rounded-md text-primary text-opacity-75 hover:text-opacity-100 cursor-pointer',
                  [ selectedId === doc.id && selectedPosition === 'split' && 'bg-primary bg-opacity-10' ])}
                title={`${selectedId === doc.id && selectedPosition === 'split' ? 'Close' : 'Open'} Split Vertically`}
                data-test-id='open-split-vertically'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  if (selectedId === doc.id && selectedPosition === 'split') {
                    behavioralContext.setDocument(null)
                    return
                  }
                  behavioralContext.setDocument({
                    id: doc?.file?.hash ?? '',
                    mimeType: doc?.file?.contentType ?? '',
                    position: 'split',
                    documentId: doc.id
                  })
                }}
              >
                <LuColumns size='1.1rem' />
              </button>
              <button
                title={`${selectedId === doc.id && selectedPosition === 'bottom' ? 'Close' : 'Open'} Split Horizontally`}
                data-test-id='open-below-view'
                className={cx('p-1 -m-1 flex items-center justify-center rounded-md text-primary text-opacity-75 hover:text-opacity-100 cursor-pointer',
                  [ selectedId === doc.id && selectedPosition === 'bottom' && 'bg-primary bg-opacity-10' ])}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  if (selectedId === doc.id && selectedPosition === 'bottom') {
                    behavioralContext.setDocument(null)
                    return
                  }

                  behavioralContext.setDocument({
                    id: doc?.file?.hash ?? '',
                    mimeType: doc?.file?.contentType ?? '',
                    position: 'bottom',
                    documentId: doc.id
                  })
                }}
              >
                <LuRows size='1.1rem' />
              </button>
            </div>
          </div>
        )
      }
    },
    { name: 'document_type', header: 'Type', isSortable: true },
    {
      name: 'created',
      isSortable: true,
      getValue: (doc) => (
        doc.created ? <FriendlyDate date={doc.created} /> : null
      ),
      header: 'Creation Date'
    },
    {
      name: 'description',
      isSortable: true,
      getValue: (doc) => doc.description,
      header: 'Description',
      gridArea: '1.5fr'
    },
    {
      name: 'actions',
      gridArea: '0.1fr',
      header: '',
      getValue: (doc) => {
        return (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            key={doc.id}
          >
            <MaterialDeleteButton
              onClick={() => {
                setModal({
                  title: 'Delete Record',
                  description: 'Are you sure you want to delete this document?',
                  open: true,
                  actions: [
                    <Grid.Button
                      key={1}
                      primary
                      light
                      data-cy='modal_cancel-btn'
                      onClick={() => {
                        setModal(undefined)
                      }}
                    >
                      Cancel
                    </Grid.Button>,
                    <Grid.Button
                      key={2}
                      primary
                      data-cy='modal_discard-btn'
                      onClick={() => {
                        deleteDocumentMutation.mutate({ patientId, id: doc.id })
                        behavioralContext.setDocument((prev) => {
                          if (prev?.id !== doc.file.hash) {
                            return prev
                          }
                          return null
                        })
                        setModal(undefined)
                      }}
                    >
                      Delete
                    </Grid.Button>
                  ]
                })
              }}
            />
          </div>
        )
      }
    }

  ], [ behavioralContext, clientName, deleteDocumentMutation, patientId ])

  const mergedStatuses = [ documentMutation.status, deleteDocumentMutation.status, query.status ].includes('pending') ? 'pending' : query.status

  function handleCloseCreateForm (callback?: () => void): void {
    if (discardDialog?.isFormDirty(CREATE_FORM_KEY) ?? false) {
      discardDialog?.show(CREATE_FORM_KEY, () => {
        setForm({ open: false })
        reset()
        if (typeof callback === 'function') {
          callback?.()
        }
      })
      return
    }
    setForm({ open: false })
    if (typeof callback === 'function') {
      callback?.()
    }
    reset()
  }

  return (
    <>
      <Card.Card expandable>
        <Card.Header
          open={card.open}
          onChangeOpen={(value) => {
            if (!value) {
              handleCloseCreateForm(() => setCard({ open: value }))
              return
            }
            setCard({ open: value })
          }}
        >
          <div className='flex flex-row justify-between w-full'>
            <h2>Behavioral Health Documents</h2>
            {!form.open && (
              <Grid.Button
                rounded
                primary
                onClick={() => {
                  setCard({ open: true })
                  setForm({ open: true })
                }}
              >
                Add Document
              </Grid.Button>
            )}
          </div>
        </Card.Header>

        <Card.ExpandableWrapper open={card.open}>
          <CreateForm
            open={form.open}
            onClose={handleCloseCreateForm}
            formState={formState}
            control={control}
            handleSubmit={handleSubmit}
            onSubmit={() => {
              setForm({ open: false })
              reset()
            }}
          />

          <Table.Table
            disableMinHeight
            columns={columns}
            data={query.data?.data ?? []}
            error={query.error as Error}
            queryStatus={mergedStatuses}
            fetchStatus={query.fetchStatus}
            expandingComponent={(value, state, setState) => (
              <UpdateForm
                doc={{
                  id: value.id,
                  description: value.description,
                  notVisible: value.not_visible,
                  created: value.created,
                  type: {
                    label: value.document_type as string,
                    value: value.document_type as string
                  }
                }}
                file={value.file}
                onClose={() => setState(null)}
                id={value.id}
                patientId={patientId}
                clientName={clientName}
              />
            )}
            noDataPlaceholder='There are no documents yet'
            sort={{ field: gridState.sortBy, direction: gridState.sortDir }}
            onSortChange={(value, direction) => setGridState((prev) => ({
              ...prev,
              sortBy: value,
              sortDir: direction
            }))}
            pagination={{ pageSize: gridState.size, page: gridState.page, totalFiltered: query.data?.total ?? 0 }}
            onPageChange={(value) => setGridState((prev) => ({ ...prev, page: value - 1 }))}
            onPageSizeChange={(value) => setGridState((prev) => ({ ...prev, size: value }))}
          />
        </Card.ExpandableWrapper>
      </Card.Card>
      <PortedModal
        description={modal?.description}
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </>
  )
}

interface UpdateFormProps {
  doc: Omit<UploadDocumentVariables, 'file'> & { id: string }
  id: string
  patientId: string
  clientName: string
  onClose: () => void
  file?: Document['file']
}

const updateFormSchema = z.object({
  description: z.string().optional(),
  created: z.string().optional(),
  type: z.object({ value: z.string(), label: z.string() }),
  notVisible: z.boolean()
})

const createFormSchema = z.object({
  description: z.string().optional(),
  created: z.string().optional(),
  type: z.object({ value: z.string(), label: z.string() }),
  notVisible: z.boolean(),
  file: z
    .any()
    .refine((files) => files?.length === 1, 'Image is required.')
})

type UpdateFormValues = z.infer<typeof updateFormSchema>
type CreateFormValues = z.infer<typeof createFormSchema>

export function UpdateForm ({ doc, patientId, clientName, id, onClose, file }: UpdateFormProps): JSX.Element {
  const { formState, control, handleSubmit, reset } = useForm<UpdateFormValues>({
    defaultValues: {
      description: doc.description ?? '',
      created: doc.created ?? '',
      type: doc.type,
      notVisible: doc.notVisible ?? false
    },
    resolver: zodResolver(updateFormSchema)
  })

  let discardDialog = useContext(DiscardDialogContext)
  let { onFormChange, resetForm } = discardDialog ?? {}

  useEffect(() => {
    if (formState.isDirty) {
      onFormChange?.(UPDATE_FORM_KEY)
      return
    }

    resetForm?.(UPDATE_FORM_KEY)
  }, [ formState.isDirty, onFormChange, resetForm ])

  useEffect(() => {
    return () => resetForm?.(UPDATE_FORM_KEY)
  }, [ resetForm ])

  const documentUpdateMutation = useEditDocumentMutation({ patientId, clientName })

  let query = useQuery({
    queryKey: [
      'preview',
      file?.hash ?? null
    ],

    async queryFn ({ queryKey }) {
      let [ , id ] = queryKey

      if (!id) {
        return
      }

      let response = await http.getRequest(`/behavioral/${patientId}/documents/${id}/download`, {
        clientName,
        documentId: doc.id
      })

      return response.data
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false
  })

  const documentsTypes = (GeneralDocuments as Array<{
    value: { display: string, code: string }
    label: string
    level: number
    category: string
  }>).map((value) => {
    return {
      value: value.value.code,
      label: value.value.display
    }
  })

  function submit (values: UpdateFormValues): void {
    onClose()
    reset()
    documentUpdateMutation.mutate({ ...values, id })
  }

  return (
    <div className='flex flex-col gap-4'>
      <form
        className='flex flex-col gap-8 px-16'

        onSubmit={handleSubmit(submit)}
      >
        <div className='flex flex-row-reverse gap-20 '>
          <div className='flex-1'>
            <DocumentViewer
              fileId={file?.hash}
              fileBase64={`data:${file?.contentType ?? ''};base64,${query?.data as string ?? ''}`}
              fileName={file?.title}
              fileSize={file?.size}
              fileType={file?.contentType}
            />
          </div>
          <div className='flex flex-col w-1/3 justify-between'>
            <Controller
              control={control}
              name='type'
              render={({ field }) => (
                <Grid.SelectWrapper
                  label='File Type'
                  required
                  className='mt-8'
                >
                  <Select
                    options={documentsTypes}
                    value={field?.value}
                    required
                    className='relative z-1000'
                    classNames={{
                      control: (state) => {
                        return state.isFocused ? 'outline outline-2 outline-primary' : 'outline-none'
                      }
                    }}
                    styles={{
                      control: (styles: any) => ({
                        ...styles,
                        'backgroundColor': '#fff',
                        'border': '1px solid #DADCE0',
                        'boxShadow': 'none',
                        '&:hover': {
                          border: '1px solid #DADCE0'
                        },
                        'borderRadius': '4px',
                        'height': '2.6rem',
                        'outline': '1px'
                      }),
                      container: (styles: any, { isFocused }) => ({
                        ...styles,
                        flex: 1,
                        backgroundColor: '#0165FF',
                        boxShadow: 'none',
                        borderRadius: '8px',
                        zIndex: isFocused ? 10 : 5,
                        outlineColor: 'transparent!important'
                      }),
                      indicatorSeparator: (styles: any) => ({
                        ...styles,
                        marginBottom: 0,
                        marginTop: 0
                      }),
                      dropdownIndicator: (styles: any) => ({
                        ...styles,
                        padding: '0 18px'
                      })
                    }}
                    onChange={(value) => field.onChange(value)}
                  />
                </Grid.SelectWrapper>
              )}
            />

            <Controller
              control={control}
              name='created'
              render={({ field, fieldState, formState }) => (
                <Grid.Input
                  value={field.value ?? ''}
                  placeholder='File Created'
                  type='date'
                  onChange={(value) => {
                    field.onChange(value)
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name='description'
              render={({ field }) => (
                <Grid.Textarea
                  placeholder='Description'
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className='flex flex-row justify-between items-center'>
          <Controller
            control={control}
            name='notVisible'
            render={({ field }) => (
              <Checkbox
                onChange={() => field.onChange(!field.value)}
                checked={(
                  field.value
                )}
                label='Not accessible from mPower'
              />
            )}
          />
          <div className='flex flex-row items-center gap-2'>
            <Grid.Button
              type='button'
              onClick={() => {
                if (discardDialog?.isFormDirty(UPDATE_FORM_KEY) ?? false) {
                  discardDialog?.show(UPDATE_FORM_KEY, () => {
                    onClose?.()
                  })
                  return
                }

                onClose?.()
              }}

              className='self-end bg-transparent rounded-md text-black hover:bg-gray-100 py-[0.8rem] px-12 transition-colors'
            >
              Cancel
            </Grid.Button>
            <Grid.Button
              type='submit'
              className='self-end bg-primary bg-opacity-10 text-primary py-[0.8rem] px-12'
            >
              Save
            </Grid.Button>
          </div>
        </div>
      </form>
    </div>
  )
}

interface CreateFormProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  formState: FormState<CreateFormValues>
  handleSubmit: UseFormHandleSubmit<CreateFormValues>
  control: Control<CreateFormValues>
}

export function CreateForm ({ open, onClose, formState, handleSubmit, control, onSubmit }: CreateFormProps): JSX.Element | null {
  const params = useParams()

  let discardDialog = useContext(DiscardDialogContext)
  let { onFormChange, resetForm } = discardDialog ?? {}

  const { id: patientId, clientName } = params as { id: string, clientName: string }

  const documentMutation = useUploadDocumentMutation({ patientId, clientName })

  let [ formOpenAnimation, setFormOpenAnimation ] = useState(false)

  useEffect(() => {
    if (formState.isDirty) {
      onFormChange?.(CREATE_FORM_KEY)
      return
    }

    resetForm?.(CREATE_FORM_KEY)
  }, [ formState.isDirty, onFormChange, resetForm ])

  useEffect(() => {
    return () => {
      resetForm?.(CREATE_FORM_KEY)
    }
  }, [ resetForm ])

  const formStyles = useSpring({
    height: open ? 435 : 0,
    opacity: open ? 1 : 0,
    onRest: () => !open && setFormOpenAnimation(false),
    onStart: () => open && setFormOpenAnimation(true)
  })

  const documentsTypes = useMemo(
    () => (GeneralDocuments as Array<{
      value: { display: string, code: string }
      label: string
      level: number
      category: string
    }>).map((value) => {
      return {
        value: value.value.code,
        label: value.value.display
      }
    }), [])

  if (!formOpenAnimation) {
    return null
  }

  function submit (values: CreateFormValues): void {
    onSubmit()
    documentMutation.mutate(values)
  }

  return (
    <animated.form
      style={formStyles}
      className='flex-1 flex relative'

      onSubmit={handleSubmit(submit)}
    >
      <div className='flex flex-col gap-6 px-16 py-8 pb-12 flex-1'>
        <div className='flex flex-row-reverse gap-20 h-full'>
          <div className='flex-1'>
            <Controller
              control={control}
              render={({ field }) => (
                <DocumentsUploader
                  file={field.value}
                  setFile={(file) => field.onChange(file)}
                />
              )}
              name='file'
            />

          </div>
          <div className='flex flex-col w-1/3 justify-betweenh-full'>
            <Controller
              control={control}
              render={({ field }) => (
                <Grid.SelectWrapper
                  label='File Type'
                  required
                  className='mt-5'
                >
                  <Select
                    options={documentsTypes}
                    value={field.value}
                    required
                    className='relative z-1000'
                    classNames={{
                      control: (state) => {
                        return state.isFocused ? 'outline outline-2 outline-primary' : 'outline-none'
                      }
                    }}
                    styles={{
                      control: (styles: any) => ({
                        ...styles,
                        'backgroundColor': '#fff',
                        'border': '1px solid #DADCE0',
                        'boxShadow': 'none',
                        '&:hover': {
                          border: '1px solid #DADCE0'
                        },
                        'borderRadius': '4px',
                        'height': '2.6rem',
                        'outline': '1px'
                      }),
                      container: (styles: any, { isFocused }) => ({
                        ...styles,
                        flex: 1,
                        backgroundColor: '#0165FF',
                        boxShadow: 'none',
                        borderRadius: '8px',
                        zIndex: isFocused ? 10 : 5,
                        outlineColor: 'transparent!important'
                      }),
                      indicatorSeparator: (styles: any) => ({
                        ...styles,
                        marginBottom: 0,
                        marginTop: 0
                      }),
                      dropdownIndicator: (styles: any) => ({
                        ...styles,
                        padding: '0 18px'
                      })
                    }}
                    onChange={(value) => field.onChange(value)}
                  />
                </Grid.SelectWrapper>
              )}
              name='type'
            />

            <Controller
              control={control}
              render={({ field }) => (
                <Grid.Input
                  value={field.value ?? ''}
                  placeholder='File Created'
                  type='date'
                  onChange={(value) => field.onChange(value)}
                />
              )}
              name='created'
            />
            <Controller
              render={({ field }) => (
                <Grid.Textarea
                  placeholder='Description'
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                />
              )}
              name='description'
              control={control}
            />

          </div>
        </div>
        <div className='flex flex-row justify-between items-center'>
          <Controller
            control={control}
            render={({ field }) => (
              <Checkbox
                onChange={(e) => field.onChange(e.target.checked)}
                checked={field.value}
                label='Not accessible from mPower'
              />
            )}
            name='notVisible'
          />

          <div className='flex flex-row items-center  gap-2'>
            <Grid.Button
              type='button'
              onClick={onClose}
              className='self-end bg-transparent rounded-md text-black hover:bg-gray-100 py-[0.8rem] px-12 transition-colors'
            >
              Cancel
            </Grid.Button>
            <Grid.Button
              disabled={!formState.isValid}
              type='submit'
              className='self-end bg-primary bg-opacity-10 text-primary py-[0.8rem] px-12 disabled:text-gray-400 disabled:bg-gray-200 transition-colors'
            >
              Create
            </Grid.Button>
          </div>
        </div>
      </div>
    </animated.form>
  )
}
